<script setup>
import { computed, ref, defineEmits, defineProps } from 'vue';
import MetricGraph from '@/products/pha/modules/dashboard/components/MetricGraph.vue';

const props = defineProps({
    metricsAvailableList: {
        type: Array,
        required: true,
    },
    userGroupAvailableList: {
        type: Array,
        required: true,
    },
    specialUserGroupAvailableList: {
        type: Array,
        required: true,
    },
});

const emit = defineEmits(['add-item', 'hide-dialog']);

const visible = ref(true);
const selectedMetric = ref(null);
const selectedUserGroup = ref(null);
const selectedSpecialUserGroup = ref(null);
const selectedGraphSource = ref({});

const displaySelection = ref(false);
const previewEnabled = ref(false);

const dateRangeOptions = [
    { label: 'Last 3 months', value: 3 },
    { label: 'Last 6 months', value: 6 },
    { label: 'Last 12 months', value: 12 },
    { label: 'All time (no filter)', value: 0 },
];
const selectedMonths = ref(3);

const selectedMetricName = computed(() => {
    if (!selectedMetric.value) {
        return null;
    }
    return props.metricsAvailableList.find((m) => m.id === selectedMetric.value)?.name ?? null;
});

const dateRangeLabel = computed(() => {
    return dateRangeOptions.find((o) => o.value === selectedMonths.value)?.label ?? '';
});

function resetPreview() {
    previewEnabled.value = false;
}

function onMetricSelected() {
    selectedUserGroup.value = null;
    selectedSpecialUserGroup.value = null;
    resetPreview();

    selectedGraphSource.value = {
        id: selectedMetric.value,
        type: 'metric',
    };

    displaySelection.value = !!selectedMetric.value;
}

function onUserGroupSelected() {
    selectedMetric.value = null;
    selectedSpecialUserGroup.value = null;
    resetPreview();

    selectedGraphSource.value = {
        id: selectedUserGroup.value,
        type: 'userGroup',
    };

    displaySelection.value = !!selectedUserGroup.value;
    previewEnabled.value = !!selectedUserGroup.value;
}

function onSpecialUserGroupSelected() {
    selectedMetric.value = null;
    selectedUserGroup.value = null;
    resetPreview();

    selectedGraphSource.value = {
        id: selectedSpecialUserGroup.value,
        type: 'specialUserGroup',
    };

    displaySelection.value = !!selectedSpecialUserGroup.value;
    previewEnabled.value = !!selectedSpecialUserGroup.value;
}

function refreshPreview() {
    if (selectedGraphSource.value?.type !== 'metric') {
        return;
    }
    previewEnabled.value = false;
    setTimeout(() => {
        previewEnabled.value = true;
    }, 0);
}

function onDateRangeChange() {
    if (previewEnabled.value && selectedGraphSource.value?.type === 'metric') {
        refreshPreview();
    }
}

function loadPreview() {
    previewEnabled.value = true;
}
</script>

<template>
    <Dialog
        v-model:visible="visible"
        :style="{ width: '640px' }"
        header="Add to Dashboard"
        :modal="true"
        :dismissableMask="true"
        @hide="$emit('hide-dialog', true)"
    >
        <div class="p-fluid">
            <div class="formgrid grid">
                <div class="field col-12">
                    <label for="metric">Metric</label>
                    <Dropdown
                        id="metric"
                        v-model="selectedMetric"
                        :options="metricsAvailableList"
                        optionValue="id"
                        optionLabel="name"
                        placeholder="Select a metric"
                        filter
                        showClear
                        @change="onMetricSelected"
                    />
                </div>
                <Divider layout="horizontal" align="center"><b>OR</b></Divider>
                <div class="field col-12">
                    <label for="userGroup">User Groups</label>
                    <Dropdown
                        id="userGroup"
                        v-model="selectedUserGroup"
                        :options="userGroupAvailableList"
                        optionValue="id"
                        optionLabel="name"
                        placeholder="Select a user group"
                        filter
                        showClear
                        @change="onUserGroupSelected"
                    />
                </div>
                <Divider layout="horizontal" align="center"><b>OR</b></Divider>
                <div class="field col-12">
                    <label for="specialUserGroup">Special User Groups</label>
                    <Dropdown
                        id="specialUserGroup"
                        v-model="selectedSpecialUserGroup"
                        :options="specialUserGroupAvailableList"
                        optionValue="id"
                        optionLabel="name"
                        placeholder="Select a user group"
                        filter
                        showClear
                        @change="onSpecialUserGroupSelected"
                    />
                </div>
            </div>

            <template v-if="displaySelection && selectedGraphSource?.id && selectedGraphSource.type === 'metric'">
                <div class="metric-preview-panel mt-3">
                    <div class="flex justify-content-between align-items-start gap-2 mb-3">
                        <div>
                            <div class="text-sm text-color-secondary">Preview metric</div>
                            <div class="font-medium">{{ selectedMetricName || 'Selected metric' }}</div>
                        </div>
                        <Tag v-if="previewEnabled" :value="dateRangeLabel" severity="info" />
                    </div>

                    <div class="flex align-items-end gap-2 flex-column sm:flex-row">
                        <div class="flex-1 w-full">
                            <label for="add-metric-date-range" class="block mb-2">Date range</label>
                            <Dropdown
                                id="add-metric-date-range"
                                v-model="selectedMonths"
                                :options="dateRangeOptions"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select date range"
                                class="w-full"
                                @change="onDateRangeChange"
                            />
                        </div>
                        <Button
                            v-if="!previewEnabled"
                            label="Load preview"
                            icon="pi pi-chart-bar"
                            class="p-button-outlined white-space-nowrap w-full sm:w-auto"
                            @click="loadPreview"
                        />
                        <Button
                            v-else
                            label="Refresh"
                            icon="pi pi-refresh"
                            class="p-button-outlined white-space-nowrap w-full sm:w-auto"
                            @click="refreshPreview"
                            v-tooltip.top="'Reload chart with the selected date range'"
                        />
                    </div>

                    <small class="text-color-secondary block mt-2">
                        Shorter ranges load faster on large companies.
                    </small>
                </div>

                <div v-if="previewEnabled" class="mt-3">
                    <MetricGraph
                        :key="`${selectedGraphSource.type}-${selectedGraphSource.id}-${selectedMonths}`"
                        :graphData="selectedGraphSource"
                        :default-months="selectedMonths"
                        :show-date-filter="false"
                    />
                </div>
            </template>

            <template v-else-if="displaySelection && selectedGraphSource?.id && previewEnabled">
                <div class="mt-3">
                    <MetricGraph
                        :key="`${selectedGraphSource.type}-${selectedGraphSource.id}`"
                        :graphData="selectedGraphSource"
                    />
                </div>
            </template>
        </div>
        <template #footer>
            <Button label="Cancel" icon="pi pi-times" class="p-button-danger" @click="visible = false" />
            <Button
                label="Add Metric"
                icon="pi pi-plus"
                class="ml-2 p-button-primary"
                :disabled="!selectedGraphSource?.id"
                @click="emit('add-item', selectedGraphSource)"
            />
        </template>
    </Dialog>
</template>

<style scoped>
.metric-preview-panel {
    padding: 1rem;
    border: 1px solid var(--surface-border);
    border-radius: 6px;
    background: var(--surface-ground);
}
</style>
